/**
 * Which hub is this, and where? — the pure half of naming a hub before any
 * session with it exists (notes/identity/PLAN.md F9/F11).
 *
 * Why this is a copy rather than an import: the hub's own implementation
 * (`otto/src/lib/hubIdentity.ts`) is Node-side — it hashes with `crypto` and
 * reads the hub keypair off disk — and lives in a different repo, so the browser
 * bundle cannot reach it. The *rules* are what must not drift: the public id's
 * shape, how an address is read, and how the id is derived from a key are
 * spelled out here once and asserted in `hubIdentity.test.ts`.
 *
 * What lives here:
 *   - F9's hub public id (`h=` in a sticker link, `publicId` on the identity
 *     route) — its shape, its comparable spelling, and the hash that ties a
 *     responder's name to the key it signed with,
 *   - reading an address a person or a QR handed over (`host:port`, `http://…`,
 *     `https://…`, a bracketed IPv6 host),
 *   - this installation's own device key, which the adoption POST carries.
 *
 * Nothing here touches the network, the DOM or a store: the services own the
 * API, the components own the rendering.
 */
import { HUB_FRONT_DOOR_PORT, HUB_TLS_PORT } from '../config/runtime';

// ─── This installation's key ─────────────────────────────────────────────────

/** Deterministic source of randomness, injectable so generation is testable. */
export type RandomBytes = (length: number) => Uint8Array;

function systemRandomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    globalThis.crypto.getRandomValues(bytes);
    return bytes;
  }
  throw new Error('No cryptographic random source available');
}

/**
 * A stable per-installation device key (base64url).
 *
 * NOT a cryptographic key pair: F5/F6 want an Ed25519 keypair and its public key
 * here, which is a separate piece of work. Until that lands, this is a random
 * per-installation identifier that satisfies what the routes actually check
 * today — `POST /api/hub/adopt` requires a 16-4096 char opaque string with no
 * whitespace, and the hub derives `device_key = dev_<sha256(pubkey)[:32]>` from
 * it. It is persisted, so one installation is one device.
 */
export function generateDevicePubKey(random: RandomBytes = systemRandomBytes): string {
  const bytes = random(32);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  const base64 = typeof btoa === 'function' ? btoa(binary) : Buffer.from(bytes).toString('base64');
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// ─── The hub's public id (F9) ────────────────────────────────────────────────

/**
 * F9's hub public id: `sha256(publicKey)[0:16]`, lowercase hex.
 *
 * One definition, because three surfaces read the same string: the sticker
 * link's `h=`, the discovery list's `GET /api/hub/identity` answer (F11), and
 * the check that refuses a sticker naming another hub
 * (`utils/adoptLink.ts` → `matchAdoptLinkToHub`).
 */
export const HUB_PUBLIC_ID_PATTERN = /^[0-9a-f]{16}$/;

/** Is this F9's hub public id? */
export function isHubPublicId(value: unknown): boolean {
  return typeof value === 'string' && HUB_PUBLIC_ID_PATTERN.test(value.trim().toLowerCase());
}

/**
 * The comparable spelling of a hub public id (trimmed, lowercased), or `''` when
 * the value is not one. Empty is the single "nothing to compare" case a caller
 * has to handle, so `''` and `null` cannot be read as two different answers.
 */
export function normalizeHubPublicId(value: unknown): string {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim().toLowerCase();
  return HUB_PUBLIC_ID_PATTERN.test(trimmed) ? trimmed : '';
}

/**
 * F9's id, **computed from the key** — the same bytes the hub derives its own
 * from (`otto/src/lib/hubIdentity.ts` → `hubPublicId`: `sha256` over the
 * base64url key as it is stored, hex, first 16, lowercase).
 *
 * Why the app has to compute one at all: every LAN answer this app reads (the
 * identity route, the signed challenge) lets a responder *name* an id and a key
 * side by side, and nothing in either answer forces the two to belong together.
 * The hash is that link — so a responder that names the real hub's `publicId`
 * while signing with a key of its own is caught before any token leaves the app
 * (`services/hubChallenge.ts` → `proveResponderKey`, card `t_515b4051`).
 *
 * Async because WebCrypto is. `null` means "this runtime cannot say" (no
 * `crypto.subtle`) and is never a pass: a caller that needs the check refuses
 * when it cannot make it — the signature check refuses on the same runtime for
 * the same reason.
 */
export async function hubPublicIdFromKey(publicKey: string): Promise<string | null> {
  const key = typeof publicKey === 'string' ? publicKey.trim() : '';
  if (!key) return null;
  const subtle = globalThis.crypto?.subtle;
  if (!subtle || typeof subtle.digest !== 'function') return null;
  try {
    const digest = await subtle.digest('SHA-256', new TextEncoder().encode(key));
    const hex = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
    return hex.slice(0, 16);
  } catch {
    return null;
  }
}

// ─── Addresses ───────────────────────────────────────────────────────────────

/**
 * `192.168.1.20:16161` (what a sticker's `u=` or the discovery step hands over)
 * → host/port. A missing port means the address's own door — the **front door**
 * `80` for a cleartext address (card `t_c2446418`), `443` for one that spells
 * `https://`, which is what those two URLs mean everywhere else too. A bracketed
 * IPv6 host is accepted.
 */
export function parseHubEndpoint(
  value: unknown,
  defaultPort?: number,
): { host: string; port: number; secure?: boolean } | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  // A scheme in the string IS the transport it came with (card `t_cf6ea0e5`):
  // `https://…` is a hub behind TLS, `http://…` is cleartext, and a bare
  // `host:port` says nothing — the caller keeps its own default rather than
  // being told the hub is cleartext.
  const secure = /^https:\/\//i.test(trimmed) ? true : /^http:\/\//i.test(trimmed) ? false : undefined;
  const raw = trimmed.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
  if (!raw) return null;

  const bare =
    raw.startsWith('[') && raw.includes(']') ? raw.slice(1, raw.indexOf(']')) + raw.slice(raw.indexOf(']') + 1) : raw;
  const separator = bare.lastIndexOf(':');
  const host = separator > 0 ? bare.slice(0, separator) : bare;
  const rawPort = separator > 0 ? bare.slice(separator + 1) : '';
  if (!host) return null;

  const fallback = defaultPort ?? (secure === true ? HUB_TLS_PORT : HUB_FRONT_DOOR_PORT);
  const port = rawPort ? Number(rawPort) : fallback;
  if (!Number.isInteger(port) || port <= 0 || port > 65535) return null;
  return secure === undefined ? { host, port } : { host, port, secure };
}

/** `http://192.168.1.20:16161` → `{host, port, secure}`; anything else → null. */
export function parseLanUrl(value: unknown): { host: string; port: number; secure?: boolean } | null {
  if (typeof value !== 'string') return null;
  const match = value.trim().match(/^https?:\/\/([^/]+)/i);
  if (!match) return null;
  return parseHubEndpoint(value);
}
